import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from '../../util/helpers/sessionStorageHelper';
import themeColors from '../../assets/styles/themeColors';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const LaunchBannerContainer = styled.div`
  width: calc(100% - 32px);
  min-height: 55px;
  border-radius: 18px;
  background: linear-gradient(
    135deg,
    ${themeColors.colorInfoBannerBackground} 0%,
    ${themeColors.colorInfoBannerBackground2} 100%
  );
  color: #fff;
  margin: 16px;
  padding: 12px 24px;
  display: flex;
  flex-direction: column;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.25);
  position: relative;
  overflow: hidden;
  animation: ${fadeInUp} 0.3s ease-in-out;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 8px 12px;
    gap: 6px;
    min-height: auto;
  }

  @media (max-width: 480px) {
    margin: 8px;
    padding: 8px 12px;
    gap: 4px;
  }
`;

const Header = styled.h2`
  color: ${themeColors.colorWhite};
  font-size: 18px;
  font-weight: 800;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  letter-spacing: 0.5px;
  line-height: 1.2;

  @media (min-width: 1200px) {
    font-size: 20px;
  }

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 1.1;
  }

  @media (max-width: 480px) {
    font-size: 13px;
  }
`;

const Body = styled.p`
  color: ${themeColors.colorWhite};
  font-size: 13px;
  font-weight: 500;
  margin: 2px 0 0 0;
  opacity: 0.95;
  letter-spacing: 0.3px;

  @media (min-width: 1200px) {
    font-size: 14px;
  }

  @media (max-width: 768px) {
    font-size: 12px;
    margin: 0;
    line-height: 1.2;
  }

  @media (max-width: 480px) {
    font-size: 10px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 24px;
  background: transparent;
  border: none;
  color: #fff;
  font-size: 30px;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }

  @media (max-width: 768px) {
    top: -4px;
    right: 12px;
  }

  @media (max-width: 480px) {
    top: -4px;
    right: 12px;
  }
`;

const LaunchBanner = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const closed = retrieveFromSessionStorage('launchBannerClosed');
    if (closed === 'true') {
      setVisible(false);
    }
  }, []);

  const handleClose = () => {
    setVisible(false);
    storeInSessionStorage('launchBannerClosed', 'true');
  };

  if (!visible) return null;

  return (
    <LaunchBannerContainer>
      <CloseButton onClick={handleClose}>×</CloseButton>
      <Header>{t('launch.title')}</Header>
      <Body>{t('launch.subtitle')}</Body>
    </LaunchBannerContainer>
  );
};

export default LaunchBanner;
